// Canonical frontend model modality and capability normalization.
// Backend model metadata remains the source data; this module is the single
// UI-side adapter for normalizing that metadata into selectable capabilities.

export const MODEL_MODALITIES = [
  'chat',
  'completion',
  'embedding',
  'tts',
  'stt',
  'image_gen',
  'vision',
  'audio_gen',
  'video_gen',
  'reasoning',
  'realtime',
  'decision',
  'extraction',
] as const

export type ModelModality = (typeof MODEL_MODALITIES)[number]

export type ReasoningEffort =
  | 'none'
  | 'minimal'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh'
  | 'max'

const CANONICAL_MODALITIES = new Set<ModelModality>(MODEL_MODALITIES)

const DISCOVERABLE_MODALITIES: ModelModality[] = [
  'chat', 'completion', 'embedding', 'tts', 'stt', 'image_gen',
  'vision', 'audio_gen', 'video_gen', 'reasoning', 'realtime', 'decision', 'extraction',
]

const LEGACY_MODALITY_MAP: Record<string, ModelModality | undefined> = {
  // Legacy backend display labels
  chat: 'chat',
  multimodal: 'chat',
  vision: 'chat',
  audio: 'chat',
  embedding: 'embedding',
  'speech-to-text': 'stt',
  'text-to-speech': 'tts',
  'image generation': 'image_gen',
  completion: 'completion',
  reranking: undefined,
  rerank: undefined,
  moderation: undefined,
  unknown: undefined,
  voice: 'tts',
  realtime: 'realtime',
  'live audio': 'realtime',
}

/**
 * Normalizes a modality string to a canonical ModelModality value.
 * Used to convert backend modality labels to frontend enum values.
 *
 * @param value - Raw modality string from backend
 * @returns Normalized modality or undefined if invalid/unknown
 */
export function normalizeModelModality(value?: string): ModelModality | undefined {
  if (!value) return undefined

  const normalized = value.trim().toLowerCase()
  if (!normalized || normalized === 'unknown') {
    return undefined
  }

  // Treat vision labels as chat models with additional capabilities.
  if (normalized === 'vision') {
    return 'chat'
  }

  if (CANONICAL_MODALITIES.has(normalized as ModelModality)) {
    return normalized as ModelModality
  }

  return LEGACY_MODALITY_MAP[normalized]
}

export interface ResolveModelCapabilitiesOptions {
  modality?: string
}

export interface ModelCapabilityMetadata {
  primaryModality?: string
  displayModality?: string
  modality?: string
  capabilities?: string[]
}

/**
 * Resolves selectable model capabilities from backend model metadata.
 *
 * Use this for provider connector model lists and governance resource model
 * rows. It preserves backend capabilities, filters stale/unknown values, and
 * derives cross-purpose capabilities only from explicit input/output
 * modalities, avoiding provider-specific model-name heuristics.
 */
export function resolveModelCapabilities(
  apiModel: Record<string, unknown>,
  options: ResolveModelCapabilitiesOptions = {},
): string[] {
  const capabilities = new Set<string>()
  const inputRaw = apiModel['inputModalities'] ?? apiModel['input_modalities']
  const outputRaw = apiModel['outputModalities'] ?? apiModel['output_modalities']
  const inputModalities = Array.isArray(inputRaw) ? inputRaw : []
  const outputModalities = Array.isArray(outputRaw) ? outputRaw : []
  const hasModalities = Array.isArray(inputRaw) && Array.isArray(outputRaw)
  const normalizeToken = (value: unknown) => String(value).trim().toLowerCase()
  const hasInput = (modality: string) => inputModalities.some((m: unknown) => normalizeToken(m) === modality)
  const hasOutput = (modality: string) => outputModalities.some((m: unknown) => normalizeToken(m) === modality)
  const acceptsText = hasModalities && hasInput('text')
  const emitsText = hasModalities && hasOutput('text')

  const add = (capability: unknown) => {
    if (typeof capability !== 'string') return
    const normalized = capability.trim().toLowerCase()
    if (!normalized || normalized === 'unknown') return
    if (normalized === 'chat' && hasModalities && !(acceptsText && emitsText)) return
    capabilities.add(normalized)
  }

  add(options.modality)

  const caps = (apiModel['capabilities'] ?? apiModel['Capabilities']) as string[] | undefined
  if (Array.isArray(caps)) {
    caps.forEach(add)
  }

  if (apiModel['supportsFunctions'] || apiModel['supports_functions']) {
    add('functions')
    add('function_calling')
  }
  if (apiModel['supportsStreaming'] || apiModel['supports_streaming']) add('streaming')
  if (apiModel['supportsVision'] || apiModel['supports_vision']) add('vision')

  if (hasModalities) {
    if (acceptsText && emitsText) add('chat')
    if (hasInput('audio') || hasOutput('audio')) add('audio')
    if (hasInput('image') && emitsText) add('vision')
  }

  return Array.from(capabilities)
}

/**
 * Backward-compatible provider-service helper name.
 */
export function extractBackendCapabilities(apiModel: Record<string, unknown>): string[] {
  return resolveModelCapabilities(apiModel)
}

/**
 * Keep uncategorized provider models visible without guessing from their IDs.
 * An explicitly categorized non-inference model (for example, moderation) is
 * excluded using provider modality/capability facts. Unknown models gain no
 * inferred controls.
 */
export function isSupportedDiscoveredModel(apiModel: Record<string, unknown>): boolean {
  const primary = apiModel['primaryModality'] ?? apiModel['primary_modality']
  const rawCapabilities = apiModel['capabilities'] ?? apiModel['Capabilities']
  const capabilities = resolveModelCapabilities(apiModel, {
    modality: typeof primary === 'string' ? primary : undefined,
  })
  if (typeof primary !== 'string' || !primary.trim() || primary.trim().toLowerCase() === 'unknown') {
    const hasClassification = (value: unknown) => Array.isArray(value) && value.some(
      (item) => typeof item === 'string' && item.trim() !== '' && item.trim().toLowerCase() !== 'unknown'
    )
    const input = apiModel['inputModalities'] ?? apiModel['input_modalities']
    const output = apiModel['outputModalities'] ?? apiModel['output_modalities']
    const hasOtherClassification =
      hasClassification(rawCapabilities) || hasClassification(input) || hasClassification(output)
    if (!hasOtherClassification) return true
  }
  return DISCOVERABLE_MODALITIES.some((modality) =>
    modelSupportsModality({ capabilities }, modality)
  )
}

export function modelSupportsModality(
  model: ModelCapabilityMetadata,
  modality: ModelModality,
): boolean {
  const normalizedTarget = normalizeModelModality(modality)
  if (!normalizedTarget) return false

  const normalizeCapabilityModality = (value: string): ModelModality | undefined => {
    const normalized = value.trim().toLowerCase()
    if (CANONICAL_MODALITIES.has(normalized as ModelModality)) {
      return normalized as ModelModality
    }
    if (
      normalized === 'chat-completion' ||
      normalized === 'chat_completion' ||
      normalized === 'chat.completion' ||
      normalized === 'function_calling' ||
      normalized === 'functions' ||
      normalized === 'tool_use' ||
      normalized === 'tools'
    ) return 'chat'
    if (normalized === 'speech-to-text') return 'stt'
    if (normalized === 'text-to-speech') return 'tts'
    if (normalized === 'image generation') return 'image_gen'
    if (normalized === 'live audio') return 'realtime'
    return undefined
  }

  const capabilities = Array.isArray(model.capabilities) ? model.capabilities : []
  if (capabilities.length > 0) {
    return capabilities.some((capability) => normalizeCapabilityModality(capability) === normalizedTarget)
  }

  return normalizeModelModality(model.primaryModality) === normalizedTarget ||
    normalizeModelModality(model.modality) === normalizedTarget ||
    normalizeModelModality(model.displayModality) === normalizedTarget
}
